import React, { useEffect, useMemo, useState } from "react";
import classNames from "classnames";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "reactstrap";
import { FaLock, FaImage, FaUser, FaCheck, FaTimes, FaExchangeAlt } from "react-icons/fa";
import { MdAttachMoney, MdDescription } from "react-icons/md";
import ReactTable from "components/ReactTable/ReactTable";
import ReactTable2 from "components/ReactTable/ReactTable2";
import {
  useBulkToggleInventoryWebsites,
  useGetInventoryWebsites,
} from "api/inventory-website/service";
import WebsiteInventoryFilters from "./WebsiteInventoryFilters";
import SearchInventoryCards from "views/inventory/components/SearchInventoryCards";
import { useNotification } from "contexts/NotificationContext";
import { resolveMediaUrl } from "utils/mediaUrl";

const formatMoney = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num) || num <= 0) return "-";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(num);
};

const centerCellStyle = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  textAlign: "center",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  maxWidth: "100%",
};

const thumbStyle = {
  width: 48,
  height: 36,
  objectFit: "cover",
  background: "#e9ecef",
  display: "block",
};

const InventoryWebsite = ({ activeColor = "blue", shellThemeVariant = "1" }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const isTheme2 = shellThemeVariant === "2";
  const InventoryTable = isTheme2 ? ReactTable2 : ReactTable;
  const bulkToggle = useBulkToggleInventoryWebsites();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [selectedIds, setSelectedIds] = useState([]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setPage(1);
    setSelectedIds([]);
  }, [debouncedSearch, status]);

  const { data, isLoading } = useGetInventoryWebsites({
    page,
    pageSize,
    search: debouncedSearch,
    status,
  });

  const tableData = useMemo(() => {
    const rows = data?.results || [];
    return rows.map((item) => {
      const general = item.general_detail || {};
      const pricing = item.pricing_detail || {};
      const images = Array.isArray(item.images) ? item.images : [];
      const firstImageRaw =
        typeof images[0] === "string"
          ? images[0]
          : images[0]?.url || images[0]?.src || images[0]?.thumbnail_url || null;
      const sellingPriceRaw = Number(pricing.selling_price || pricing.internet_price || 0);
      const model = [general.model, general.trim].filter(Boolean).join(" ") || "-";
      const isActive = item.is_active !== false;

      return {
        id: item.documentId || String(item.id),
        image: firstImageRaw ? resolveMediaUrl(firstImageRaw) || firstImageRaw : null,
        stock: general.stock_number || "-",
        type: general.type || "-",
        year: general.year || "-",
        make: general.make || "-",
        model,
        trim: general.trim || "-",
        msrp: formatMoney(pricing.msrp),
        sellingPrice: formatMoney(sellingPriceRaw),
        sellingPriceRaw,
        pics: images.length,
        isLocked: Boolean(item.is_locked),
        isActive,
      };
    });
  }, [data?.results]);

  const pageIds = useMemo(() => tableData.map((row) => row.id), [tableData]);
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.includes(id));
  const selectedInactiveCount = useMemo(
    () =>
      tableData.filter((row) => selectedIds.includes(row.id) && !row.isActive).length,
    [selectedIds, tableData]
  );
  const selectedActiveCount = useMemo(
    () =>
      tableData.filter((row) => selectedIds.includes(row.id) && row.isActive).length,
    [selectedIds, tableData]
  );

  const toggleRow = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const toggleAllPage = () => {
    if (allPageSelected) {
      setSelectedIds((prev) => prev.filter((id) => !pageIds.includes(id)));
      return;
    }
    setSelectedIds((prev) => Array.from(new Set([...prev, ...pageIds])));
  };

  const handleBulkToggle = async () => {
    if (selectedIds.length === 0) return;
    try {
      const result = await bulkToggle.mutateAsync(selectedIds);
      setSelectedIds([]);
      showNotification(
        "success",
        t("inventoryWebsite.toggleSuccess", {
          count: result?.toggled ?? selectedIds.length,
        })
      );
    } catch (error) {
      showNotification("error", t("inventoryWebsite.toggleError"));
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: () => (
          <div
            className="inventory-website-check-cell"
            onClick={(e) => e.stopPropagation()}
          >
            <input
              type="checkbox"
              className="inventory-website-check"
              checked={allPageSelected}
              onChange={toggleAllPage}
            />
          </div>
        ),
        id: "select",
        accessor: "select",
        disableSortBy: true,
        disableFilters: true,
        canSort: false,
        width: 44,
        Cell: ({ row }) => (
          <div
            className="inventory-website-check-cell"
            onClick={(e) => e.stopPropagation()}
          >
            <input
              type="checkbox"
              className="inventory-website-check"
              checked={selectedIds.includes(row.original.id)}
              onChange={() => toggleRow(row.original.id)}
            />
          </div>
        ),
      },
      {
        Header: "",
        accessor: "image",
        disableSortBy: true,
        disableFilters: true,
        canSort: false,
        width: 70,
        Cell: ({ value }) => (
          <div className="w-100 d-flex justify-content-center">
            {value ? (
              <img
                src={value}
                alt=""
                style={thumbStyle}
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = require("assets/img/car.png");
                  e.currentTarget.style.objectFit = "contain";
                  e.currentTarget.style.padding = "4px";
                }}
              />
            ) : (
              <div
                style={{
                  ...thumbStyle,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#9aa0a6",
                }}
              >
                <FaImage size={14} />
              </div>
            )}
          </div>
        ),
      },
      {
        Header: t("searchInventory.stock"),
        accessor: "stock",
        Cell: ({ value }) => <div style={centerCellStyle}>{value}</div>,
      },
      {
        Header: t("inventoryWebsite.statusFilter"),
        accessor: "isActive",
        width: 100,
        Cell: ({ value }) => (
          <div style={centerCellStyle}>
            <span
              style={{
                display: "inline-block",
                padding: "2px 8px",
                borderRadius: 999,
                fontSize: 11,
                fontWeight: 700,
                background: value ? "#dcfce7" : "#f3f4f6",
                color: value ? "#166534" : "#6b7280",
              }}
            >
              {value
                ? t("inventoryWebsite.statusActive")
                : t("inventoryWebsite.statusInactive")}
            </span>
          </div>
        ),
      },
      {
        Header: t("inventoryWebsite.type"),
        accessor: "type",
        Cell: ({ value }) => <div style={centerCellStyle}>{value}</div>,
      },
      {
        Header: t("searchInventory.year"),
        accessor: "year",
        Cell: ({ value }) => <div style={centerCellStyle}>{value}</div>,
      },
      {
        Header: t("searchInventory.make"),
        accessor: "make",
        Cell: ({ value }) => <div style={centerCellStyle}>{value}</div>,
      },
      {
        Header: t("searchInventory.modelTrim"),
        accessor: "model",
        Cell: ({ value }) => <div style={centerCellStyle}>{value}</div>,
      },
      {
        Header: t("inventoryWebsite.trim"),
        accessor: "trim",
        Cell: ({ value }) => <div style={centerCellStyle}>{value}</div>,
      },
      {
        Header: t("inventoryWebsite.msrp"),
        accessor: "msrp",
        Cell: ({ value }) => (
          <div
            style={{
              width: "100%",
              textAlign: "right",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {value}
          </div>
        ),
      },
      {
        Header: t("inventoryWebsite.sellingPrice"),
        accessor: "sellingPrice",
        Cell: ({ value }) => (
          <div
            style={{
              width: "100%",
              textAlign: "right",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {value}
          </div>
        ),
      },
      {
        Header: t("searchInventory.pics"),
        accessor: "pics",
        Cell: ({ value }) => (
          <div className="w-100 text-center d-flex align-items-center justify-content-center" style={{ gap: 4 }}>
            <FaImage size={12} />
            <span>{value ?? 0}</span>
          </div>
        ),
      },
      {
        Header: "",
        accessor: "icons",
        disableSortBy: true,
        width: 110,
        Cell: ({ row }) => (
          <div className="d-flex align-items-center justify-content-center" style={{ gap: 8 }}>
            <MdAttachMoney size={14} color={row.original?.sellingPriceRaw > 0 ? "#16a34a" : "#9aa0a6"} />
            <FaLock size={12} color={row.original?.isLocked ? "#111827" : "#9aa0a6"} />
            <MdDescription size={13} color="#9aa0a6" />
            <FaUser size={12} color="#9aa0a6" />
          </div>
        ),
      },
      {
        Header: "",
        accessor: "actions",
        disableSortBy: true,
        width: 90,
        Cell: ({ row }) => (
          <div className="w-100 d-flex justify-content-end">
            <Button
              size="sm"
              color="primary"
              onClick={(e) => {
                e.stopPropagation();
                if (row.original?.id) {
                  navigate(`/inventory-website/${row.original.id}`);
                }
              }}
            >
              {t("inventoryWebsite.edit")}
            </Button>
          </div>
        ),
      },
    ],
    [allPageSelected, navigate, selectedIds, t]
  );

  const filters = (
    <>
      <WebsiteInventoryFilters
        activeColor={activeColor}
        isTheme2={isTheme2}
        search={search}
        onSearchChange={setSearch}
        status={status}
        onStatusChange={setStatus}
      />
      <div className="mb-3">
        {selectedIds.length > 0 ? (
          <Button
            color="primary"
            outline
            disabled={bulkToggle.isPending}
            onClick={handleBulkToggle}
            style={{
              fontWeight: 700,
              letterSpacing: 0.3,
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            {selectedActiveCount > 0 && selectedInactiveCount === 0 ? (
              <FaTimes />
            ) : selectedInactiveCount > 0 && selectedActiveCount === 0 ? (
              <FaCheck />
            ) : (
              <FaExchangeAlt />
            )}
            {selectedActiveCount > 0 && selectedInactiveCount === 0
              ? t("inventoryWebsite.deactivate")
              : selectedInactiveCount > 0 && selectedActiveCount === 0
                ? t("inventoryWebsite.activate")
                : t("inventoryWebsite.toggleStatus")}
            {` (${selectedIds.length})`}
          </Button>
        ) : null}
      </div>
    </>
  );

  const table = (
    <div
      className="inventory-table-wrap inventory-website-table"
      style={{ fontSize: "13px", minWidth: 0, maxWidth: "100%" }}
    >
      <style>{`
        .inventory-website-table .rt-thead .rt-th:first-child,
        .inventory-website-table .rt-thead .rt-th:first-child.-cursor-pointer {
          cursor: default !important;
        }
        .inventory-website-table .rt-thead .rt-th:first-child .rt-resizable-header-content {
          padding-right: 0 !important;
        }
        .inventory-website-table .rt-thead .rt-th:first-child:before,
        .inventory-website-table .rt-thead .rt-th:first-child:after,
        .inventory-website-table .rt-thead .rt-th:first-child .rt-resizable-header-content:before,
        .inventory-website-table .rt-thead .rt-th:first-child .rt-resizable-header-content:after {
          display: none !important;
          content: none !important;
        }
        .inventory-website-table .rt-th:first-child,
        .inventory-website-table .rt-td:first-child {
          width: 44px !important;
          min-width: 44px !important;
          max-width: 44px !important;
          padding-left: 8px !important;
          padding-right: 8px !important;
          text-align: center !important;
          vertical-align: middle !important;
        }
        .inventory-website-check-cell {
          width: 100%;
          height: 100%;
          min-height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0;
          padding: 0;
        }
        .inventory-website-check {
          width: 16px !important;
          height: 16px !important;
          margin: 0 !important;
          padding: 0 !important;
          float: none !important;
          position: static !important;
          cursor: pointer;
          accent-color: #2563eb;
          flex: 0 0 16px;
        }
      `}</style>
      <InventoryTable
        activeColor={activeColor}
        data={tableData}
        columns={columns}
        className={classNames("-striped -highlight", { "sticky-stock-table": isTheme2 })}
        manualPagination={true}
        pageCount={data?.pagination?.pageCount || 1}
        pageIndex={page - 1}
        totalItems={data?.pagination?.total}
        noDataText={t("searchInventory.noRecordsFound")}
        loading={isLoading}
        loadingText={t("searchInventory.loadingSearchInventory")}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
          setSelectedIds([]);
        }}
        pageSize={pageSize}
        onPageChange={(pageIndex) => {
          setPage(pageIndex + 1);
          setSelectedIds([]);
        }}
      />
    </div>
  );

  return (
    <div
      className={classNames("content search-inventory-page", {
        "search-inventory-page--theme2": isTheme2,
        "theme2-admin-content-compact": isTheme2,
        "theme2-inventory-search-inset": isTheme2,
      })}
    >
      <SearchInventoryCards
        isTheme2={isTheme2}
        filters={filters}
        table={table}
        title={t("inventoryWebsite.title")}
      />
    </div>
  );
};

export default InventoryWebsite;
